import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Loader2, ChevronRight, Sparkles, Search, ArrowUpDown, Users,
} from 'lucide-react';
import AssignMentorDialog, { isOfflineMode, formatDay, formatTime } from './AssignMentorDialog';
import ExperienceDetailsDialog from './ExperienceDetailsDialog';
import { experienceDisplay, lifecycleState } from '@/lib/experienceStatus';
import { isActionable } from './attention';

export const SC = {
  requested: 'bg-yellow-50 text-yellow-800', pending_coordinator_action: 'bg-yellow-50 text-yellow-800',
  waitlisted: 'bg-orange-50 text-orange-700',
  pending_parent_confirmation: 'bg-violet-50 text-violet-700', confirmed: 'bg-blue-50 text-blue-700',
  awaiting_feedback: 'bg-green-50 text-green-700', completed: 'bg-green-50 text-green-700',
  awaiting_reschedule: 'bg-violet-50 text-violet-700', cancelled: 'bg-red-50 text-red-700',
  rejected: 'bg-red-50 text-red-700', not_conducted: 'bg-red-50 text-red-700',
};
export const STATUS_LABEL = {
  requested: 'New request', pending_coordinator_action: 'With coordinator', waitlisted: 'Waitlisted',
  pending_parent_confirmation: 'Awaiting parent', confirmed: 'Confirmed',
  awaiting_reschedule: 'Needs new slot', awaiting_feedback: 'Awaiting feedback',
  completed: 'Completed', cancelled: 'Cancelled', rejected: 'Declined',
  not_conducted: 'Not conducted',
};
export const ASSIGNABLE = ['requested', 'pending_coordinator_action', 'waitlisted'];
export const RESCHEDULABLE = ['pending_parent_confirmation', 'confirmed', 'awaiting_reschedule'];
const STATUS_ORDER = {
  requested: 0, pending_coordinator_action: 1, waitlisted: 2, awaiting_reschedule: 3,
  pending_parent_confirmation: 4, confirmed: 5, awaiting_feedback: 6, completed: 7,
  cancelled: 8, rejected: 9, not_conducted: 10,
};

/** Sort order follows the lifecycle a coordinator reads down the page. */
const DISPLAY_ORDER = {
  new_request: 0, unfulfilled: 1, needs_slot: 2, awaiting_parent: 3, confirmed: 4,
  awaiting_completion: 5, awaiting_feedback: 6, completed: 7, not_conducted: 8,
  cancelled: 9, closed: 10,
};

export function isPastSession(dateStr) {
  if (!dateStr) return false;
  return dateStr <= new Date().toISOString().split('T')[0];
}

/** Lifecycle filters. `all` stays the full record count; `open` deliberately overlaps
 *  the lifecycle buckets because it is an administrative view, not a lifecycle stage. */
const FILTERS = [
  { id: 'open', label: 'Needs action', test: b => isActionable(b) },
  { id: 'new_requests', label: 'New requests', test: b => lifecycleState(b) === 'open' },
  { id: 'upcoming', label: 'Upcoming', test: b => lifecycleState(b) === 'upcoming' },
  { id: 'in_progress', label: 'In progress', test: b => lifecycleState(b) === 'in_progress' },
  { id: 'awaiting_completion', label: 'Awaiting completion', test: b => lifecycleState(b) === 'awaiting_completion' },
  { id: 'completed', label: 'Completed', test: b => lifecycleState(b) === 'completed' },
  { id: 'not_conducted', label: 'Cancelled / not conducted', test: b => ['not_conducted', 'cancelled'].includes(lifecycleState(b)) },
  { id: 'admin_closed', label: 'Closed', test: b => lifecycleState(b) === 'closed' },
];

/** Which coordinator actions an experience allows, from its status and date. */
export function experienceActions(b) {
  if (b.admin_closed_at) return [];
  const life = lifecycleState(b);
  if (ASSIGNABLE.includes(b.status)) return ['assign'];
  if (b.status === 'awaiting_reschedule') return life === 'awaiting_completion' ? [] : ['reschedule', 'cancel'];
  if (b.status === 'pending_parent_confirmation') return ['reschedule', 'cancel'];
  if (b.status === 'confirmed') {
    if (life === 'awaiting_completion') return ['remind_mentor', 'admin_close'];
    if (life === 'in_progress') return [];  // under way: the mentor records the outcome
    return ['reschedule', 'cancel'];
  }
  if (b.status === 'awaiting_feedback') return ['remind', 'admin_close'];
  return [];
}



/** The single status badge used across coordinator tables and dialogs. */
export function StatusBadge({ booking, className = '', testId }) {
  const d = experienceDisplay(booking);
  return (
    <Badge className={`border-0 text-[10px] ${d.tone} ${className}`} data-testid={testId} data-status={d.key}>
      {d.label}
    </Badge>
  );
}

const PRIORITY = ['requested', 'waitlisted', 'awaiting_reschedule', 'pending_parent_confirmation',
  'confirmed', 'awaiting_feedback', 'completed', 'not_conducted', 'cancelled', 'rejected'];

/** What a grouped session reads as: the least-advanced state that still needs someone. */
function groupStatus(children) {
  const statuses = children.map(c => c.status);
  if (statuses.every(s => ['completed', 'not_conducted', 'cancelled', 'rejected'].includes(s))) {
    return statuses.find(s => s === 'completed') || statuses[0];
  }
  return PRIORITY.find(s => statuses.includes(s)) || statuses[0];
}

export default function ExperienceTable({
  bookings, loading, onRefresh, offline, initialFilter,
  mixed = false, hideFilters = false, allBookings, onOpenDetails, rowAction,
}) {
  const [assignBooking, setAssignBooking] = useState(null);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [localDetail, setLocalDetail] = useState(null);
  const openDetails = onOpenDetails || setLocalDetail;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(initialFilter === 'new' ? 'open' : (initialFilter || 'all'));
  const [sort, setSort] = useState({ key: 'status', dir: 1 });
  const [expanded, setExpanded] = useState({});

  const handleAssigned = () => {
    setAssignBooking(null); setRescheduleBooking(null);
    onRefresh();
  };

  // Students sharing one grouped session
  const groupSizes = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      if (b.session_id && !['cancelled', 'rejected', 'not_conducted'].includes(b.status)) {
        map[b.session_id] = (map[b.session_id] || 0) + 1;
      }
    });
    return map;
  }, [bookings]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = bookings.filter(b => {
      const f = FILTERS.find(x => x.id === filter);
      if (f && !f.test(b)) return false;
      if (!q) return true;
      return [b.child_name, b.career_name, b.mentor_name, b.parent_name, b.assigned_venue?.workplace_name]
        .filter(Boolean).some(v => v.toLowerCase().includes(q));
    });
    const val = (b) => ({
      status: DISPLAY_ORDER[experienceDisplay(b).key] ?? 99,
      student: (b.child_name || '').toLowerCase(),
      career: (b.career_name || '').toLowerCase(),
      mentor: (b.mentor_name || '').toLowerCase(),
      date: b.assigned_date || '9999',
    }[sort.key]);
    return [...list].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort.dir);
  }, [bookings, query, filter, sort]);

  const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }));

  // One row per grouped session, expandable into its students
  const displayRows = useMemo(() => {
    const out = [];
    const seen = {};
    rows.forEach(b => {
      const size = b.session_id ? (groupSizes[b.session_id] || 1) : 1;
      if (size < 2) { out.push({ type: 'row', b }); return; }
      if (seen[b.session_id] !== undefined) {
        out[seen[b.session_id]].children.push(b);
        return;
      }
      seen[b.session_id] = out.length;
      out.push({ type: 'group', sessionId: b.session_id, sample: b, children: [b] });
    });
    return out;
  }, [rows, groupSizes]);
  const Th = ({ label, sortKey, className = '' }) => (
    <th className={`px-3 py-2 text-left text-xs font-bold text-[#9CA3AF] uppercase tracking-wider ${className}`}>
      {sortKey ? (
        <button onClick={() => toggleSort(sortKey)} className="flex items-center gap-1 hover:text-[#1F2937] transition-colors" data-testid={`sort-${sortKey}`}>
          {label}<ArrowUpDown className="h-3 w-3" />
        </button>
      ) : label}
    </th>
  );

  if (loading) return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;

  return (
    <div data-testid="experience-table">
      <div className="flex flex-col gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder={offline ? 'Search student, career, host or workplace' : 'Search student, career or mentor'}
            className="pl-8 h-11 text-base sm:h-9 sm:text-xs rounded-lg border-gray-200 bg-white" data-testid="experience-search" />
        </div>
        {!hideFilters && <div className="flex gap-1.5 flex-nowrap overflow-x-auto sm:flex-wrap [&::-webkit-scrollbar]:hidden -mx-1 px-1 sm:mx-0 sm:px-0">
          {[{ id: 'all', label: `All (${bookings.length})` }, ...FILTERS.map(f => ({ id: f.id, label: `${f.label} (${bookings.filter(f.test).length})` }))].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`px-2.5 py-1 shrink-0 whitespace-nowrap rounded-full text-[11px] font-semibold border transition-colors ${filter === f.id ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`}
              data-testid={`exp-filter-${f.id}`}>{f.label}</button>
          ))}
        </div>}
      </div>

      {rows.length === 0 ? (
        <p className="text-center py-12 text-[#9CA3AF] text-sm" data-testid="experience-empty">
          {query || filter !== 'all' ? 'Nothing matches this search or filter.' : `No ${offline ? 'workplace visit' : 'online session'} requests yet.`}
        </p>
      ) : (
        <>
          {/* Table — tablet and desktop */}
          <div className="hidden md:block bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
            <table className="w-full" data-testid="experience-table-desktop">
              <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">
                <tr>
                  {mixed && <Th label="Type" />}
                  <Th label="Student" sortKey="student" />
                  <Th label="Career" sortKey="career" />
                  <Th label={offline ? 'Host' : 'Mentor'} sortKey="mentor" />
                  {offline && <Th label="Workplace" />}
                  <Th label="Date & time" sortKey="date" />
                  {offline && <Th label="Students" />}
                  <Th label="Status" sortKey="status" />
                  <Th label="" className="text-right" />
                </tr>
              </thead>
              <tbody>
                {displayRows.map(item => item.type === 'group' ? (
                  <React.Fragment key={`group-${item.sessionId}`}>
                    <tr className="border-b border-[#F3F4F6] last:border-0 bg-[#FBFBFD] hover:bg-[#F7F8FA] transition-colors cursor-pointer"
                      onClick={() => setExpanded(e => ({ ...e, [item.sessionId]: !e[item.sessionId] }))}
                      data-testid={`group-row-${item.sessionId}`}>
                      {mixed && (
                        <td className="px-3 py-2.5">
                          <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#6B7280]">
                            {isOfflineMode(item.sample.assigned_mode || item.sample.requested_mode) ? 'Visit' : 'Online'}
                          </Badge>
                        </td>
                      )}
                      <td className="px-3 py-2.5">
                        <p className="text-xs font-semibold text-[#1F2937] flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-[#9CA3AF]" />
                          Group session · {item.children.length} students
                        </p>
                        <p className="text-[10px] text-[#9CA3AF]">
                          {expanded[item.sessionId] ? 'Hide students' : 'Show each student'}
                          <Link to={`/group-sessions/${item.sessionId}`} onClick={e => e.stopPropagation()}
                                className="ml-2 underline underline-offset-2 hover:text-[#1F2937]"
                                data-testid={`open-group-${item.sessionId}`}>
                            Open group details
                          </Link>
                        </p>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-[#4B5563]">{item.sample.career_name}</td>
                      <td className="px-3 py-2.5 text-sm text-[#4B5563]">{item.sample.mentor_name || <span className="text-[#C8CDD6]">Unassigned</span>}</td>
                      {offline && <td className="px-3 py-2.5 text-sm text-[#4B5563]">{item.sample.assigned_venue?.workplace_name || <span className="text-[#C8CDD6]">—</span>}</td>}
                      <td className="px-3 py-2.5 text-sm text-[#4B5563] whitespace-nowrap">
                        {item.sample.assigned_date ? <>{formatDay(item.sample.assigned_date)}<span className="text-[#9CA3AF]">{item.sample.assigned_time_slot ? ` · ${formatTime(item.sample.assigned_time_slot)}` : ''}</span></> : <span className="text-[#C8CDD6]">Not scheduled</span>}
                      </td>
                      {offline && <td className="px-3 py-2.5 text-sm text-[#4B5563]">{item.children.length} students</td>}
                      <td className="px-3 py-2.5"><StatusBadge booking={{ ...item.sample, status: groupStatus(item.children) }} testId={`group-status-${item.sessionId}`} /></td>
                      <td className="px-3 py-2.5 text-right">
                        <ChevronRight className={`h-4 w-4 text-[#C8CDD6] inline transition-transform ${expanded[item.sessionId] ? 'rotate-90' : ''}`} />
                      </td>
                    </tr>
                    {expanded[item.sessionId] && item.children.map(b => (
                      <tr key={b.id} className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFBFC] transition-colors cursor-pointer"
                        onClick={() => openDetails(b)} data-testid={`booking-${b.id}`}>
                        {mixed && <td className="px-3 py-2.5" />}
                        <td className="px-3 py-2.5 pl-7">
                          <p className="text-xs font-semibold text-[#1F2937]">{b.child_name}</p>
                          <p className="text-[10px] text-[#9CA3AF]">{b.child_grade ? `Grade ${b.child_grade} · ` : ''}{b.parent_name}</p>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-[#9CA3AF]">—</td>
                        <td className="px-3 py-2.5 text-sm text-[#9CA3AF]">—</td>
                        {offline && <td className="px-3 py-2.5 text-sm text-[#9CA3AF]">—</td>}
                        <td className="px-3 py-2.5 text-sm text-[#9CA3AF]">—</td>
                        {offline && <td className="px-3 py-2.5 text-sm text-[#9CA3AF]">—</td>}
                        <td className="px-3 py-2.5"><StatusBadge booking={b} testId={`status-${b.id}`} /></td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {rowAction?.(b)}
                            <ChevronRight className="h-4 w-4 text-[#C8CDD6]" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ) : (
                  <React.Fragment key={item.b.id}>
                    {(b => (
                    <tr className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFBFC] transition-colors cursor-pointer"
                      onClick={() => openDetails(b)} data-testid={`booking-${b.id}`}>
                      {mixed && (
                        <td className="px-3 py-2.5" data-testid={`type-${b.id}`}>
                          <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#6B7280]">
                            {isOfflineMode(b.assigned_mode || b.requested_mode) ? 'Visit' : 'Online'}
                          </Badge>
                        </td>
                      )}
                      <td className="px-3 py-2.5">
                        <p className="text-xs font-semibold text-[#1F2937]">{b.child_name}</p>
                        <p className="text-[10px] text-[#9CA3AF]">{b.child_grade ? `Grade ${b.child_grade} · ` : ''}{b.parent_name}</p>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-[#4B5563]">{b.career_name}</td>
                      <td className="px-3 py-2.5 text-sm text-[#4B5563]">
                        {b.mentor_name || (b.suggested_mentor_name
                          ? <span className="text-[#F06651] flex items-center gap-1"><Sparkles className="h-3 w-3" />{b.suggested_mentor_name}</span>
                          : <span className="text-[#C8CDD6]">Unassigned</span>)}
                      </td>
                      {offline && <td className="px-3 py-2.5 text-sm text-[#4B5563]" data-testid={`workplace-${b.id}`}>{b.assigned_venue?.workplace_name || <span className="text-[#C8CDD6]">—</span>}</td>}
                      <td className="px-3 py-2.5 text-sm text-[#4B5563] whitespace-nowrap">
                        {b.assigned_date ? <>{formatDay(b.assigned_date)}<span className="text-[#9CA3AF]">{b.assigned_time_slot ? ` · ${formatTime(b.assigned_time_slot)}` : ''}</span></> : <span className="text-[#C8CDD6]">Not scheduled</span>}
                      </td>
                      {offline && (
                        <td className="px-3 py-2.5 text-sm text-[#4B5563]" data-testid={`capacity-${b.id}`}>
                          {b.session_id ? `${groupSizes[b.session_id] || 1} student${(groupSizes[b.session_id] || 1) === 1 ? '' : 's'}` : <span className="text-[#C8CDD6]">—</span>}
                        </td>
                      )}
                      <td className="px-3 py-2.5">
                        <StatusBadge booking={b} testId={`status-${b.id}`} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {rowAction?.(b)}
                          <ChevronRight className="h-4 w-4 text-[#C8CDD6]" />
                        </div>
                      </td>
                    </tr>
                    ))(item.b)}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards — mobile */}
          <div className="md:hidden space-y-2" data-testid="experience-cards-mobile">
            {rows.map(b => (
              <div key={b.id} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`booking-card-${b.id}`}>
                <button onClick={() => openDetails(b)} className="w-full px-4 py-3 text-left" data-testid={`open-card-${b.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1F2937] truncate">{b.child_name}</p>
                      <p className="text-sm text-[#6B7280] truncate">{b.career_name}</p>
                      {b.session_id && (groupSizes[b.session_id] || 1) > 1 && (
                        <p className="text-[11px] text-[#9CA3AF] mt-0.5" data-testid={`group-tag-${b.id}`}>
                          Group session · {groupSizes[b.session_id]} students
                          <Link to={`/group-sessions/${b.session_id}`} onClick={e => e.stopPropagation()}
                                className="ml-2 underline underline-offset-2 hover:text-[#1F2937]"
                                data-testid={`open-group-card-${b.id}`}>
                            Open group
                          </Link>
                        </p>
                      )}
                    </div>
                    <StatusBadge booking={b} className="shrink-0" />
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-[#4B5563]">
                    <span>{b.mentor_name || 'Unassigned'}</span>
                    {b.assigned_date && <span>{formatDay(b.assigned_date)}{b.assigned_time_slot ? ` · ${formatTime(b.assigned_time_slot)}` : ''}</span>}
                    {offline && b.assigned_venue?.workplace_name && <span>{b.assigned_venue.workplace_name}</span>}
                  </div>
                </button>
                {rowAction && <div className="px-4 pb-3 -mt-1">{rowAction(b)}</div>}
              </div>
            ))}
          </div>
        </>
      )}

      {localDetail && (
        <ExperienceDetailsDialog booking={localDetail} bookings={allBookings || bookings}
          onClose={() => setLocalDetail(null)} onRefresh={onRefresh}
          onAssign={setAssignBooking} onReschedule={setRescheduleBooking} />
      )}
      {assignBooking && <AssignMentorDialog booking={assignBooking} onClose={() => setAssignBooking(null)} onAssigned={handleAssigned} />}
      {rescheduleBooking && <AssignMentorDialog mode="reschedule" booking={rescheduleBooking} onClose={() => setRescheduleBooking(null)} onAssigned={handleAssigned} />}
    </div>
  );
}
